<script setup lang="ts">
import Menu from "../components/Menu.vue";
import { onMounted, onUnmounted, ref, markRaw, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { eventBus } from '@/libs/EventBus';
import { ElMessageBox } from 'element-plus';
import { Close } from '@element-plus/icons-vue';
import { handleUnRegisterDevice } from '@/utils/device.utils';
import { MenuClose, MenuOpen } from '@xpos/icons-svg';
import { getPlatform } from '@/utils/misc';

const router = useRouter();
const { t } = useI18n();
const showDeviceNotRegisteredDialog = ref(false);

// 菜单展开/收缩状态
const isMenuExpanded = ref(true);

// 检查是否为移动端
const isMobile = computed(() => {
    return getPlatform() === 'mobile-web';
});

const isSafari = ref(
    /Safari/i.test(navigator.userAgent) &&
    !/Chrome/i.test(navigator.userAgent) &&
    !/Chromium/i.test(navigator.userAgent) &&
    !/Edge/i.test(navigator.userAgent) &&
    !/Edg/i.test(navigator.userAgent) &&
    !/OPR/i.test(navigator.userAgent)
)

// Agentee promo: web platforms only, el-alert handles hide via own visible ref
const showAgenteeBanner = ['desktop-web', 'mobile-web'].includes(getPlatform()) && !localStorage.getItem('agentee_banner_dismissed');
const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent), isAndroid = /Android/i.test(navigator.userAgent);
const dismissAgenteeBanner = () => localStorage.setItem('agentee_banner_dismissed', '1');

// 切换菜单状态
const toggleMenu = () => {
    isMenuExpanded.value = !isMenuExpanded.value;
};

// 处理菜单点击事件
const handleMenuClick = () => {
    // 只在移动端(mobile-web)生效，点击菜单项后自动缩回菜单
    if (isMobile.value) {
        isMenuExpanded.value = false;
    }
};

// 菜单宽度状态
const isMenuWide = ref(false);

// 处理菜单宽度变化
const handleMenuWidthChange = (isWide: boolean) => {
    isMenuWide.value = isWide;
};

// 菜单宽度 (临时PC端也可以收缩)
const menuWidth = computed(() => {
    if (!isMenuExpanded.value) return '0px';
    return isMenuWide.value ? '200px' : '77px';
});
const handleDeviceNotRegistered = () => {
    console.log('device not registered event received');
    if (!showDeviceNotRegisteredDialog.value) {
        showDeviceNotRegisteredDialog.value = true;
        ElMessageBox.confirm(
            t('account.deviceNotRegistered.message'),
            t('account.deviceNotRegistered.title'),
            {
                confirmButtonText: t('account.deviceNotRegistered.confirmButtonText'),
                showCancelButton: false,
                type: 'error',
                icon: markRaw(Close),
                closeOnClickModal: false,
            }
        )
            .then(async () => {
                showDeviceNotRegisteredDialog.value = false;
                await handleUnRegisterDevice();
            })
            .catch(() => {
                showDeviceNotRegisteredDialog.value = false;
                router.replace('/login');
            });
    }
};

onMounted(() => {
    eventBus.on('device not registered', handleDeviceNotRegistered);
});

onUnmounted(() => {
    eventBus.off('device not registered', handleDeviceNotRegistered);
});
</script>

<template>
    <el-container class="container1" :class="{ 'safari-container': isSafari }">
        <el-aside class="aside" :style="{ width: menuWidth }">
            <Menu v-show="isMenuExpanded" @menu-click="handleMenuClick" @menu-width-change="handleMenuWidthChange"></Menu>
        </el-aside>
        <!-- 菜单切换按钮 (临时在PC端也显示) -->
        <div class="menu-toggle-btn"
             :class="{ 'collapsed': !isMenuExpanded }"
             :style="{ left: isMenuExpanded ? (isMenuWide ? '199px' : '76px') : '0px' }"
             @click="toggleMenu"
             v-if="isMobile">
            <MenuOpen v-if="!isMenuExpanded" style="width: 30px; height: 40px;" />
            <MenuClose v-else style="width: 30px; height: 40px;" />
        </div>
        <el-container class="content" :class="{ 'safari-height': isSafari }">
            <!-- <component-header /> -->
            <el-alert v-if="showAgenteeBanner" show-icon :title="t('agentee.title')" @close="dismissAgenteeBanner">
                <el-link v-if="!isAndroid" class="mr-4" type="primary" href="https://apps.apple.com/app/id6802558247" target="_blank">{{ t('agentee.appStore') }}</el-link>
                <el-link v-if="!isIOS" type="primary" href="https://play.google.com/store/apps/details?id=com.agenteehq.adminapp" target="_blank">{{ t('agentee.googlePlay') }}</el-link>
            </el-alert>
            <div class="main">
                <router-view v-slot="{ Component, route }">
                    <keep-alive :include="['pos']">
                        <component :is="Component" :key="route.path" />
                    </keep-alive>
                </router-view>
            </div>
            <!-- <component-footer /> -->
        </el-container>
    </el-container>
</template>

<style scoped>
.layout {
    min-height: 100vh;
    background-color: #ffffff;
}

.container1 {
    height: 100vh;
}

.safari-container{
    height: -webkit-fill-available;
}

@supports (-webkit-touch-callout: none) {
    body {
      height: -webkit-fill-available;
    }
}

.safari-height{
    overflow-y: auto !important;
}

.aside {
    background-color: var(--color-primary);
    overflow: hidden;
    overflow-y: auto;
    -ms-overflow-style: none;
    overflow: -moz-scrollbars-none;
    transition: width 0.3s ease;
    position: relative;
}

.aside::-webkit-scrollbar {
    display: none;
}

.head {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 50px;
}

.head>div {
    display: flex;
    align-items: center;
}

.head img {
    width: 50px;
    height: 50px;
    margin-right: 10px;
}

.head span {
    font-size: 16px;
}

.content {
    display: flex;
    flex-direction: column;
    max-height: 100vh;
    overflow: hidden;
}

.main {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;  /* Firefox */
}
.main::-webkit-scrollbar { display: none; }  /* Chrome/Safari */

/* 菜单切换按钮样式 */
.menu-toggle-btn {
    position: fixed;
    top: 0;
    bottom: 0;
    margin: auto;
    width: 30px;
    height: 40px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    color: white;
    z-index: 999;
}

.menu-toggle-btn:hover {
    background: rgba(255, 255, 255, 0.2);
}

.menu-toggle-btn svg {
    width: 30px;
    height: 40px;
}

/* 收缩状态下的按钮样式 */
.menu-toggle-btn.collapsed {
    position: fixed;
    right: auto;
    background: var(--color-primary);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
</style>
